import { useMemo } from 'react';

import { capitalizeFirst } from 'clipsal-cortex-utils/src/formatting/formatting';

import licenses from '../../../../../public/licenses/licenses.json';

interface Licenses {
  [key: string]: License;
}

export interface License {
  licenses: string;
  repository: string;
  path: string;
  publisher?: string;
  licenseFile?: string;
  packageName?: string;
  packageId?: string;
  packageVersion?: string;
}

type PublisherToLicenses = {
  [publisher: string]: License[];
};

type LicensesByPublisher = {
  publisherId: string;
  publisher: string;
  licenses: License[];
};

function normalizeLicenseFilePath(licenseFilePath: string | undefined): string | undefined {
  if (!licenseFilePath || licenseFilePath.includes('README')) return undefined;

  const normalizedPath = licenseFilePath.replace(/\\/g, '/');
  const nodeModulesPath = '/node_modules/';
  const nodeModulesIndex = normalizedPath.lastIndexOf(nodeModulesPath);

  if (nodeModulesIndex < 0 && !normalizedPath.startsWith('node_modules/')) return undefined;

  const relativePath =
    nodeModulesIndex >= 0
      ? normalizedPath.slice(nodeModulesIndex + nodeModulesPath.length)
      : normalizedPath.slice('node_modules/'.length);

  if (!relativePath || relativePath.startsWith('README')) return undefined;
  return relativePath.includes('.md') ? relativePath : `${relativePath}.txt`;
}

export function useLicenses(): LicensesByPublisher[] {
  return useMemo(() => {
    const licensesByPublisher = Object.entries(licenses as unknown as Licenses)
      .map(([packageName, values]) => {
        const licenseFile = normalizeLicenseFilePath(values?.licenseFile);
        const packageParts = packageName.split('@');
        return {
          ...values,
          packageName,
          packageId: toURL(packageParts[packageParts.length - 2]),
          packageVersion: packageParts[packageParts.length - 1],
          publisher: capitalizeFirst(values.publisher ?? 'Unknown'),
          licenseFile: licenseFile,
        };
      })
      .reduce((grouped: PublisherToLicenses, license: License) => {
        const { publisher = 'Unknown' } = license;
        if (!grouped[publisher]) {
          grouped[publisher] = [];
        }
        grouped[publisher].push(license);
        return grouped;
      }, {});
    return Object.entries(licensesByPublisher)
      .sort(([publisherA], [publisherB]) => publisherA.localeCompare(publisherB))
      .map(([publisher, licenses]) => ({
        publisherId: toURL(publisher.replace('@', '')),
        publisher,
        licenses,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenses]);
}

export function toURL(str: string | undefined = '') {
  return encodeURIComponent(str.trim().toLowerCase().replace(/\s+/g, '-'));
}
